import { Button, Paper, Stack, Typography } from '@mui/material';

import { useUpdateInventoryStatus } from '@gozoki/api';
import DoneIcon from '@mui/icons-material/Done';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import BlockerLoader from '../../components/loaders/BlockerLoader';
import { useRights } from '../../utils/hooks';
import { fonts } from '../../utils/theme/fonts.theme';

interface InventoryValidateOrCancelProps {
    inventoryId: number;
    shopId: number;
    setTab: (tab: number) => void;
}

export const InventoryValidateOrCancel = ({
    inventoryId,
    shopId,
    setTab,
}: InventoryValidateOrCancelProps) => {
    const isUserSuperAdmin = useRights(['SUPER_ADMIN']);
    const queryClient = useQueryClient();
    const { mutate: updateInventoryStatus, isPending } = useUpdateInventoryStatus();

    const onSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['storeInventories', shopId] });
        queryClient.invalidateQueries({
            queryKey: ['inventory', inventoryId],
        });
        setTab(3);
    }, [queryClient, shopId, setTab, inventoryId]);

    const handleValidate = useCallback(
        () =>
            updateInventoryStatus(
                { id: inventoryId, status: 'validated' },
                {
                    onSuccess,
                }
            ),
        [updateInventoryStatus, inventoryId, onSuccess]
    );

    const handleCancel = useCallback(
        () =>
            updateInventoryStatus(
                { id: inventoryId, status: 'canceled' },
                {
                    onSuccess,
                }
            ),
        [updateInventoryStatus, inventoryId, onSuccess]
    );

    if (!isUserSuperAdmin) {
        return null;
    }

    return (
        <Paper sx={{ p: 2 }}>
            <BlockerLoader isLoading={isPending} />
            <Stack gap={2}>
                <Stack flexDirection="row">
                    <Typography style={fonts.pageSubtitle} flexGrow={1}>
                        Validation Admin
                    </Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        sx={{ height: '36px', bgcolor: 'white', marginRight: '8px' }}
                        onClick={handleCancel}
                        disabled={isPending}
                    >
                        Annuler l'inventaire
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ height: '36px' }}
                        startIcon={<DoneIcon />}
                        disabled={isPending}
                        onClick={handleValidate}
                    >
                        Valider l'inventaire
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};
